import React from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  navRowContainerSx,
  withDesignFont,
} from "services/constants/designTokens";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { useHoistedBreadcrumb } from "./settingsBreadcrumb";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";

// Shared building blocks for the Payroll > Configuration settings screens
// (first user: Pay Input Configuration). A page is:
//   <ConfigPageShell> breadcrumb / title / subtitle / Cancel + Save footer
//     <ConfigSectionNav> in-page list of the things being configured
//     <ConfigSettingCard> one card per setting: icon tile, title,
//       description on the left, the control on the right
// Controls: ConfigSelectField, ConfigRadioGroup, ConfigCheckboxList.
//
// Spacing sits on the 8px grid (MUI spacing units). Text comes from the
// app's shared designType scale (designTokens.js), not local sizes:
//   page title    designType.pageTitle        26/700
//   card title    designType.approvalCardTitle 15/700 (Approval Center cards)
//   label         designType.tableBodyCell    14, medium weight
//   option/body   designType.tableBodyCell    14
//   helper        designType.helperText       13
//   caption       designType.captionText      12
const text = {
  cardTitle: { ...designType.approvalCardTitle, color: designColors.slate900 },
  label: { ...designType.tableBodyCell, fontWeight: designWeights.medium },
  body: designType.tableBodyCell,
  helper: designType.helperText,
  caption: { ...designType.captionText, fontWeight: designWeights.medium },
};

// Radios/checkboxes without MUI's built-in 9px padding, so they line up
// with the label and helper text above and below them.
const choiceControlSx = {
  p: 0,
  color: designColors.slate400,
  "&.Mui-checked": { color: designColors.teal },
  "& .MuiSvgIcon-root": { fontSize: 20 },
};

// Tints for the card icon tiles, picked by name so screens stay consistent.
export const CONFIG_ICON_TINTS = {
  blue: { bg: "#EAF2FF", fg: "#2878FF" },
  green: { bg: "#E7F8EF", fg: "#12B76A" },
  purple: { bg: "#F1EDFF", fg: "#6D5DF6" },
  amber: { bg: "#FFF4E5", fg: "#F0954A" },
  pink: { bg: "#FDECF2", fg: "#E0457B" },
  teal: { bg: designColors.tealTint, fg: designColors.tealDark },
};

const cardSx = {
  ...designShell.sectionCard,
  boxShadow: "none",
};

export const ConfigPageShell = ({
  breadcrumb = [],
  title,
  subtitle,
  onCancel,
  onSave,
  saveDisabled = false,
  cancelDisabled = false,
  saving = false,
  saveLabel = "Save Changes",
  // Optional block at the right of the title (e.g. "View Overtime →").
  headerAction = null,
  children,
}) => {
  // Inside Payroll Settings the breadcrumb shows at the top of the screen.
  const breadcrumbHoisted = useHoistedBreadcrumb(breadcrumb);
  return (
  <ThemeProvider theme={withDesignFont}>
    <Mui.Box
      sx={{
        ...designShell.pageSurface,
        px: 2,
        pt: 2,
        pb: 4,
      }}
    >
      {breadcrumbHoisted ? null : <GlobalBreadcrumb trail={breadcrumb} />}
      <Mui.Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography component="h1" sx={designType.pageTitle}>
            {title}
          </Mui.Typography>
          {subtitle ? (
            <Mui.Typography sx={{ ...text.helper, mt: 0.5 }}>
              {subtitle}
            </Mui.Typography>
          ) : null}
        </Mui.Box>
        {headerAction}
      </Mui.Box>

      <Mui.Box sx={{ mt: 3 }}>{children}</Mui.Box>

      {onSave ? (
        <Mui.Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 3 }}
        >
          <SecondaryButton onClick={onCancel} disabled={cancelDisabled}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={onSave}
            disabled={saveDisabled}
            loading={saving}
          >
            {saveLabel}
          </PrimaryButton>
        </Mui.Stack>
      ) : null}
    </Mui.Box>
  </ThemeProvider>
  );
};

// Two-column body: the section nav on the left, the cards on the right.
// On md+ the nav card stretches to the full height of the card column so
// both columns end on the same line. Below md the nav sits on top as a
// horizontally scrolling row.
export const ConfigTwoColumn = ({ nav, children }) => (
  <Mui.Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: { md: "stretch" },
      gap: 2,
    }}
  >
    {nav}
    <Mui.Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
      {children}
    </Mui.Stack>
  </Mui.Box>
);

// items: [{ key, label, icon }]
export const ConfigSectionNav = ({ title, items, activeKey, onSelect }) => (
  <Mui.Box
    component="nav"
    aria-label={title}
    sx={{
      ...cardSx,
      width: { xs: "100%", md: 240 },
      flexShrink: 0,
      p: 1,
    }}
  >
    {title ? (
      <Mui.Typography sx={{ ...designType.sectionHeading, px: 1.5, py: 1 }}>
        {title}
      </Mui.Typography>
    ) : null}
    <Mui.Box
      sx={{
        display: "flex",
        flexDirection: { xs: "row", md: "column" },
        gap: 0.5,
        overflowX: { xs: "auto", md: "visible" },
      }}
    >
      {items.map((item) => {
        const active = item.key === activeKey;
        const Icon = item.icon;
        return (
          <Mui.ButtonBase
            key={item.key}
            onClick={() => onSelect(item.key)}
            aria-current={active ? "page" : undefined}
            sx={{
              ...navRowContainerSx({ active }),
              justifyContent: "flex-start",
              gap: 1.5,
              px: 1.5,
              py: 1,
              flexShrink: 0,
              textAlign: "left",
              "&:hover": active
                ? {}
                : { backgroundColor: designColors.slateBg },
            }}
          >
            {Icon ? (
              <Icon
                sx={{
                  fontSize: 20,
                  color: active ? designColors.tealDark : designColors.slate600,
                }}
              />
            ) : null}
            <Mui.Typography
              sx={{
                ...text.body,
                fontWeight: active ? designWeights.semibold : designWeights.medium,
                color: active ? designColors.tealDark : designColors.slate700,
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Mui.Typography>
          </Mui.ButtonBase>
        );
      })}
    </Mui.Box>
  </Mui.Box>
);

export const ConfigSettingCard = ({
  icon: Icon,
  tint = "teal",
  title,
  description,
  children,
  footer,
}) => {
  const colors = CONFIG_ICON_TINTS[tint] || CONFIG_ICON_TINTS.teal;
  return (
    <Mui.Box
      component="section"
      sx={{
        ...cardSx,
        p: 3,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: "minmax(0, 5fr) minmax(0, 6fr)",
        },
        columnGap: 4,
        rowGap: 2,
      }}
    >
      <Mui.Stack direction="row" spacing={2} alignItems="flex-start">
        {Icon ? (
          <Mui.Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: designRadii.iconButton,
              backgroundColor: colors.bg,
              color: colors.fg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Mui.Box>
        ) : null}
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography component="h2" sx={text.cardTitle}>
            {title}
          </Mui.Typography>
          {description ? (
            <Mui.Typography sx={{ ...text.helper, mt: 0.5 }}>
              {description}
            </Mui.Typography>
          ) : null}
        </Mui.Box>
      </Mui.Stack>
      <Mui.Box sx={{ minWidth: 0, maxWidth: 480 }}>{children}</Mui.Box>
      {footer ? <Mui.Box sx={{ gridColumn: "1 / -1" }}>{footer}</Mui.Box> : null}
    </Mui.Box>
  );
};

// Full-width card for a list being configured (e.g. Reimbursement Types):
// icon tile, title and description on the left, an action (e.g. "+ Add")
// on the right, then the table across the whole card and an optional note.
export const ConfigTableCard = ({
  icon: Icon,
  tint = "teal",
  title,
  description,
  action,
  note,
  children,
}) => {
  const colors = CONFIG_ICON_TINTS[tint] || CONFIG_ICON_TINTS.teal;
  return (
    <Mui.Box component="section" sx={{ ...cardSx, p: 3 }}>
      <Mui.Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "flex-start" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Mui.Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
          {Icon ? (
            <Mui.Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: designRadii.iconButton,
                backgroundColor: colors.bg,
                color: colors.fg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Mui.Box>
          ) : null}
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography component="h2" sx={text.cardTitle}>
              {title}
            </Mui.Typography>
            {description ? (
              <Mui.Typography sx={{ ...text.helper, mt: 0.5 }}>
                {description}
              </Mui.Typography>
            ) : null}
          </Mui.Box>
        </Mui.Stack>
        {action ? <Mui.Box sx={{ flexShrink: 0 }}>{action}</Mui.Box> : null}
      </Mui.Stack>
      {children}
      {note ? (
        <Mui.Typography sx={{ ...text.helper, mt: 2 }}>{note}</Mui.Typography>
      ) : null}
    </Mui.Box>
  );
};

const ControlLabel = ({ children, id }) => (
  <Mui.Typography id={id} sx={{ ...text.label, mb: 1 }}>
    {children}
  </Mui.Typography>
);

// options: [{ value, label }]. Uses the shared CustomAutoComplete. A value
// is always required here, so clearing the field keeps the current one.
export const ConfigSelectField = ({ label, value, options, onChange, id, helperText }) => {
  const labelId = `${id}-label`;
  return (
    <Mui.Box>
      <ControlLabel id={labelId}>{label}</ControlLabel>
      <CustomAutoComplete
        dataList={options}
        selectedValue={options.find((option) => option.value === value) ?? null}
        updateDataFunction={(event, option) => {
          if (option) onChange(option.value);
        }}
        placeHolder={`Select ${String(label).toLowerCase()}`}
      />
      {helperText ? (
        <Mui.Typography sx={{ ...text.helper, mt: 1 }}>{helperText}</Mui.Typography>
      ) : null}
    </Mui.Box>
  );
};

// options: [{ value, label, disabled }]; helperText sits under the radios.
export const ConfigRadioGroup = ({
  label,
  value,
  options,
  onChange,
  helperText,
  id,
}) => {
  const labelId = `${id}-label`;
  return (
    <Mui.Box>
      <ControlLabel id={labelId}>{label}</ControlLabel>
      <Mui.RadioGroup
        row
        aria-labelledby={labelId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{ columnGap: 4, rowGap: 1 }}
      >
        {options.map((option) => (
          <Mui.FormControlLabel
            key={option.value}
            value={option.value}
            control={<Mui.Radio sx={{ ...choiceControlSx, mr: 1 }} />}
            label={option.label}
            disabled={Boolean(option.disabled)}
            sx={{ m: 0, "& .MuiFormControlLabel-label": text.body }}
          />
        ))}
      </Mui.RadioGroup>
      {helperText ? (
        <Mui.Typography sx={{ ...text.helper, mt: 1 }}>
          {helperText}
        </Mui.Typography>
      ) : null}
    </Mui.Box>
  );
};

export const ComingSoonChip = () => (
  <Mui.Box
    component="span"
    sx={{
      ...text.caption,
      ml: 1,
      px: 1,
      py: 0.25,
      borderRadius: designRadii.pill,
      backgroundColor: "#EAF2FF",
      color: "#2878FF",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
    }}
  >
    Coming Soon
  </Mui.Box>
);

// options: [{ value, label, description, comingSoon }]. comingSoon options
// are shown unchecked and disabled.
export const ConfigCheckboxList = ({ values, options, onToggle, error }) => (
  <Mui.Stack spacing={2}>
    {options.map((option) => {
      const disabled = Boolean(option.comingSoon);
      const checked = !disabled && values.includes(option.value);
      return (
        <Mui.Stack
          key={option.value}
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          component="label"
          sx={{ cursor: disabled ? "default" : "pointer" }}
        >
          <Mui.Checkbox
            checked={checked}
            disabled={disabled}
            onChange={() => onToggle(option.value)}
            sx={choiceControlSx}
          />
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography
              sx={{
                ...text.label,
                color: disabled ? designColors.slate600 : designColors.slate900,
              }}
            >
              {option.label}
              {option.comingSoon ? <ComingSoonChip /> : null}
            </Mui.Typography>
            {option.description ? (
              <Mui.Typography sx={{ ...text.helper, mt: 0.25 }}>
                {option.description}
              </Mui.Typography>
            ) : null}
          </Mui.Box>
        </Mui.Stack>
      );
    })}
    {error ? (
      <Mui.Typography sx={{ ...text.helper, color: "#DC2626" }}>
        {error}
      </Mui.Typography>
    ) : null}
  </Mui.Stack>
);
